import { Link, routes } from "@cedarjs/router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Avatar, AvatarGroup, Badge, Button, ButtonGroup, Chip, Divider, IconButton, Paper, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from "@mui/material";
import dayjs from "dayjs";
import { t } from "i18next";
import { useAuth } from "src/auth";
import ReviewComment from "src/components/Review/ReviewComment/ReviewComment";
import { getReviewConfig } from "src/const/Defaults";
import { stringAvatar } from "src/const/Helpers";
import { MODELS_OBJECT } from "src/const/Models";
import { hasResourcePermission, useResourcePermissions } from "src/lib/roles";
import { useDateStore, useStateStore } from "src/lib/store";

import { FindReviewsQuery } from "types/graphql";
import ReviewButton from "../ReviewButton/ReviewButton";
import { AnimatePresence } from "framer-motion";
import AnimatedSlidingBlock from "src/components/AnimatedSlidingBlock/AnimatedSlidingBlock";
import { faCancel, faCheck, faReply, faXmark } from "@fortawesome/free-solid-svg-icons";
import ReviewCommentCheck from "../ReviewCommentCheck/ReviewCommentCheck";

interface ReviewCommentContent {
  reviewComment: FindReviewsQuery["reviews"][0]["comments"][0],
  resourceFocused?: String,
  onReply?: (number) => {}
  onCheck?: (number) => {}
  onCancel?: (number) => {}
}
export const ReviewCommentReplyContent = ({ reviewComment, resourceFocused = null }: ReviewCommentContent) => (
  <div className="flex gap-2 w-full items-start ">
    <FontAwesomeIcon icon={faReply} size="xs" rotation={180} className="text-gray-300 mt-2" />
    <div className="">
      <Typography component="span" className="inline" variant="body2">{reviewComment.content}&nbsp;–</Typography>
      <Typography component="span" variant="body1" className="inline text-xs font-medium">&nbsp;{reviewComment.profile.fullName}</Typography>
      <Typography component="span" variant="body2" className="inline text-xs">&nbsp;{dayjs(reviewComment.createdAt).format("dddd DD/MM/YY")}</Typography>
    </div>
  </div>
)
export const ReviewCommentContent = ({ reviewComment, resourceFocused = null }: ReviewCommentContent) => {
  const { hasRole, currentUser } = useAuth()
  const roles = useResourcePermissions({hasRole, currentUser, resource:"ReviewComment", data: reviewComment})
  const hasRoleReviewCommentCreate = roles.canCreateResource
  const hasRoleReviewCommentCheckCreate = hasResourcePermission(hasRole, "ReviewCommentCheck", "create")

  return (
    <div className="flex gap-2 w-full items-start flex-col px-4 py-2 hover:bg-gray-500/5 dark:hover:bg-black/10 ">
      <div className="flex gap-2 w-full items-start relative group">
        <Avatar {...stringAvatar(reviewComment.profile.fullName, { width: "28px", height: "28px", fontSize: "14px" })} />
        <div className="grow">
          <div className="flex justify-between mb-1 w-full">
            <Typography variant="body1" className="text-sm font-medium">{reviewComment.profile.fullName}</Typography>
            <Typography variant="body2" className="text-sm">&nbsp;{dayjs(reviewComment.createdAt).format("dddd DD/MM/YY")}</Typography>
          </div>
          <Typography className="whitespace-pre-line" variant="body2">{reviewComment.content}</Typography>
          <div className="flex justify-between items-end">
            {reviewComment.models &&
              <div className="mt-2 flex gap-1 flex-wrap">
                {reviewComment.models?.map(model =>
                  <Chip key={model} label={<span><FontAwesomeIcon icon={MODELS_OBJECT[model].icon} className="mr-1 opacity-60" />{t(`models.${model}`, { defaultValue: model })}</span>} size="small" />
                )}
              </div>
            }
            {reviewComment.checks &&
              <div className="mt-2 flex gap-1 flex-wrap">
                <AvatarGroup max={4} sx={{ ["& .MuiAvatar-root"]: { border: "none" } }}>
                  {reviewComment.checks.map(check =>
                    <Badge
                      key={check.id}
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      color={check.checked ? "success" : "error"}
                      sx={{
                        "& .MuiBadge-badge": { fontSize: 8, width: "10px", height: "10px", padding: "0", minWidth: "0", right: "25%", bottom: "25%" },
                      }}

                      badgeContent={<FontAwesomeIcon icon={check.checked ? faCheck : faXmark} size="xs" />}
                    // color={check.checked ? "success" : "error"}
                    // variant="dot"
                    >
                      <Tooltip title={dayjs(check.createdAt).format("LLLL")}>
                        <Avatar {...stringAvatar(check.profile.fullName, { width: "20px", height: "20px", fontSize: "8px" })} />
                      </Tooltip>
                    </Badge>
                  )}
                </AvatarGroup>

              </div>
            }
          </div>
        </div>
        {(hasRoleReviewCommentCreate || roles.canInput || roles.canCreateResource) && <Paper className="absolute -top-4 right-0  group-hover:block hidden p-1">
          <ButtonGroup variant="outlined">
            {roles.canInput && <ReviewComment reviewComment={reviewComment} isInline={false}/>}
            {roles.canCreateResource && <ReviewComment parent={reviewComment} />}
            {hasRoleReviewCommentCheckCreate && <ReviewCommentCheck reviewComment={reviewComment} checks={true} />}
            {hasRoleReviewCommentCheckCreate && <ReviewCommentCheck reviewComment={reviewComment} checks={false} />}
          </ButtonGroup>
        </Paper>}
      </div>
      <div className="w-full ml-4">
        {reviewComment.replies?.map(reply =>
          <ReviewCommentReplyContent reviewComment={reply} key={reply.id} />
        )}
      </div>
    </div>
  )
}
export const ReviewContent = ({ review }) => {
  const reviewConfig = getReviewConfig()
  return (
  <div className=" w-full">
    <div className="flex justify-between mb-1 w-full">
      <div className="flex gap-2 items-center">
        {!!review.state && <FontAwesomeIcon className={"text-lg " + reviewConfig[review.state]?.color} icon={reviewConfig[review.state]?.icon} />}
        <Typography variant="body1" className="text-md">&nbsp;{dayjs(review.date).format("dddd DD/MM/YY")}</Typography>
      </div>
      <Typography variant="body1" className="text-md">&nbsp;{review.batch?.name}</Typography>
    </div>
    <Typography className="whitespace-pre-line my-2" variant="body2">{review.description}</Typography>
    <Typography className="flex" variant="caption">{t("labels.review")} {t("labels.createdBy")} <Avatar {...stringAvatar(review.profile.fullName, { width: "20px", height: "20px", fontSize: "8px" })} className="mx-1" /> {review.profile.fullName}</Typography>
  </div>
)
}

interface ReviewCardProps {
  review: FindReviewsQuery["reviews"][0]
  defaultExpanded?: boolean
}
const ReviewCard = ({ review, defaultExpanded = false }: ReviewCardProps) => {
  const { hasRole } = useAuth()
  const tank = useStateStore(state => state.tank)
  const [setDate, workingDate] = useDateStore(state => [state.setDate, state.getDayJSDate()])
  const reviewConfig = getReviewConfig()
  return (
    <Accordion
      //Lazy load of component, so EditReviewCell isn't triggered for all rows 
      //https://mui.com/material-ui/react-accordion/#performance
      //https://github.com/mui/material-ui/issues/10569#issuecomment-515547180
      defaultExpanded={defaultExpanded}
      {...(defaultExpanded ? {expanded:defaultExpanded} : {})}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      className={`${review.state ? reviewConfig[review.state].border : "border-transparent"} ${defaultExpanded ? '!rounded-none min-h-screen' : ''} border-0 border-l-4 border-solid shadow-none w-full`}
    >
      <AccordionSummary
        // expandIcon={<FontAwesomeIcon icon={faAngleDown} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <ReviewContent review={review} />
      </AccordionSummary>
      {(hasResourcePermission(hasRole, "Review", "update") || hasResourcePermission(hasRole, "Review", "create")) && <AccordionActions>
        {/* {hasResourcePermission(hasRole, "ReviewComment", "create") && <ReviewComment review={review} />} */}
        {hasResourcePermission(hasRole, "Review", "update") && tank &&
          <Button component={Link} to={routes.review({ idSite: tank.site?.id, idTank: tank.id })} onClick={() => setDate(dayjs(review.date))}>
            {t("actions.edit")}
          </Button>
        }
        {/* {hasResourcePermission(hasRole, "Review", "create") && tank && !workingDate.isSame(dayjs(review.date), 'day') &&
          <Button component={Link} to={routes.review({idSite:tank.site?.id, idTank: tank.id})} color="success">
            {t("actions.show")}
          </Button>
          } */}
        {hasResourcePermission(hasRole, "Review", "create") && tank && !workingDate.isSame(dayjs(review.date), 'day') &&
          <ReviewButton tank={tank} />
        }
      </AccordionActions>
      }
      <AccordionDetails>
        <Divider className="mb-4" />
        <div className="flex gap-1 flex-col">
          <AnimatePresence>
            {review.comments.map(comment =>
              <AnimatedSlidingBlock key={comment.id.toString()}>
                <ReviewCommentContent reviewComment={comment} />
              </AnimatedSlidingBlock>
            )}
          </AnimatePresence>
        </div>
      </AccordionDetails>
      <AccordionActions>
        <ReviewComment review={review} isInline className="w-full" />
      </AccordionActions>

    </Accordion>

  )
}

export default ReviewCard
